import React, {useCallback, useState} from 'react'
import Popover from '@material-ui/core/Popover'
import {ChromePicker, ColorChangeHandler} from 'react-color'
import Color from 'color'
import {isHexColor} from 'lib/color'

type ColorPickerProps = {
  value: string
  onChange: (color: string) => void
  'aria-label'?: string
  className?: string
  /**
   * Optional custom trigger. Defaults to a native-looking color swatch
   * for the custom-button toolbar.
   */
  anchor?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void
    hexValue: string
  }) => React.ReactNode
}

export default function ColorPicker(props: ColorPickerProps) {
  const {value, onChange, className, anchor} = props
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const hexValue = convertToHex(value)

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleChange: ColorChangeHandler = useCallback(
    (color) => {
      onChange(color.hex)
    },
    [onChange],
  )

  const open = Boolean(anchorEl)

  return (
    <>
      {anchor ? (
        anchor({onClick: handleOpen, hexValue})
      ) : (
        <button
          type="button"
          className={className || 'html-editor-color-picker__swatch-btn'}
          aria-label={props['aria-label']}
          onClick={handleOpen}
          style={{backgroundColor: hexValue}}
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        disableRestoreFocus
        // Keep focus in the editor so applying a color doesn't blur the
        // selection TipTap is styling.
        disableAutoFocus
        disableEnforceFocus
      >
        <ChromePicker
          color={hexValue}
          onChange={handleChange}
          disableAlpha
          {...{defaultView: 'hex'}}
        />
      </Popover>
    </>
  )
}

function convertToHex(color: string) {
  if (!color) {
    return '#000000'
  }

  if (isHexColor(color)) {
    return color
  }

  try {
    return Color(color).hex()
  } catch {
    return '#000000'
  }
}
